/**
 * What the assistant is told about the screen behind it, and which screens it
 * may send the user to.
 *
 * Pure functions over a location; no React, no storage. The page context rides
 * on every message (`PageContext`), so "approve this?" on a review page can mean
 * that run. The route allow-list is the server's, repeated here on purpose: a
 * client action is untrusted input like any other, and the browser re-checks it
 * before following it.
 */

import type { PageContext } from '../../types/api';
import { KNOWLEDGE_BASE_SECTIONS } from '../knowledge-base/sections';

/** The contract's cap on `PageContext.route`. */
const ROUTE_MAX_LENGTH = 200;

const UUID = '[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}';
const UUID_PATTERN = new RegExp(`^${UUID}$`, 'i');
const REVIEW_PATTERN = new RegExp(`^/review/(${UUID})$`, 'i');
const DAY_PATTERN = /^\d{4}-\d{2}-\d{2}$/;

/** Enough of a location to describe: what `useLocation()` hands back. */
export interface LocationLike {
  pathname: string;
  search: string;
}

/**
 * The page context for a location: its path and query, plus the one thing on
 * screen worth naming. Only values that look like what they claim are named,
 * since the URL is user-editable.
 */
export function pageContextFor(location: LocationLike): PageContext {
  const route = `${location.pathname}${location.search}`.slice(0, ROUTE_MAX_LENGTH);
  const params = new URLSearchParams(location.search);

  const review = REVIEW_PATTERN.exec(location.pathname);
  if (review?.[1]) return { route, entity: { type: 'run', id: review[1] } };

  if (location.pathname === '/calendar') {
    const day = params.get('day');
    if (day !== null && DAY_PATTERN.test(day)) {
      return { route, entity: { type: 'calendar_day', id: day } };
    }
  }

  if (location.pathname === '/knowledge-base') {
    const section = params.get('section');
    if (section !== null && KNOWLEDGE_BASE_SECTIONS.some((entry) => entry.key === section)) {
      return { route, entity: { type: 'kb_section', id: section } };
    }
  }

  return { route };
}

/** Bare paths the assistant may open, with no query. */
const PLAIN_ROUTES = new Set([
  '/',
  '/generation',
  '/templates',
  '/history',
  '/calendar',
  '/news',
  '/settings',
  '/assistant',
]);

const HISTORY_STATUSES = new Set(['waiting', 'done', 'failed', 'rejected']);
const MONTH_PATTERN = /^\d{4}-(0[1-9]|1[0-2])$/;

/**
 * The route back when the contract's `open_page` allow-list admits it, else
 * null. Exact shapes only: one query parameter where one is allowed, nothing
 * else, no fragments, no other origin.
 */
export function allowedAssistantRoute(route: string): string | null {
  if (typeof route !== 'string' || !route.startsWith('/') || route.startsWith('//')) return null;

  let parsed: URL;
  try {
    parsed = new URL(route, 'https://outpost.invalid');
  } catch {
    return null;
  }
  if (parsed.origin !== 'https://outpost.invalid' || parsed.hash) return null;

  const path = parsed.pathname;
  const keys = [...parsed.searchParams.keys()];

  if (keys.length === 0) {
    if (PLAIN_ROUTES.has(path)) return path;
    const review = REVIEW_PATTERN.exec(path);
    if (review?.[1] && UUID_PATTERN.test(review[1])) return path;
    return null;
  }

  if (keys.length !== 1) return null;
  const [key] = keys;
  const value = parsed.searchParams.get(key ?? '') ?? '';

  if (path === '/history' && key === 'status' && HISTORY_STATUSES.has(value)) {
    return `/history?status=${value}`;
  }
  if (path === '/calendar' && key === 'month' && MONTH_PATTERN.test(value)) {
    return `/calendar?month=${value}`;
  }
  if (
    path === '/knowledge-base' &&
    key === 'section' &&
    KNOWLEDGE_BASE_SECTIONS.some((entry) => entry.key === value)
  ) {
    return `/knowledge-base?section=${value}`;
  }
  return null;
}

/** The name of an allowed route, for "Opened … for you". */
export function assistantRouteLabel(route: string): string {
  const parsed = new URL(route, 'https://outpost.invalid');
  const path = parsed.pathname;
  if (path === '/') return 'Home';
  if (path.startsWith('/review/')) return 'the post';
  if (path === '/knowledge-base') {
    const section = parsed.searchParams.get('section');
    const entry = KNOWLEDGE_BASE_SECTIONS.find((candidate) => candidate.key === section);
    return entry ? entry.label : 'the brand profile';
  }
  const names: Record<string, string> = {
    '/generation': 'a new post',
    '/templates': 'Templates',
    '/history': 'History',
    '/calendar': 'the calendar',
    '/news': 'News',
    '/settings': 'Settings',
    '/assistant': 'the AI assistant',
  };
  return names[path] ?? 'that page';
}
